// src/components/ui/sections/ServiceEditor/ServiceEditorShadcn.jsx
import React, { useState, useEffect } from 'react';
import {
  Settings,
  DollarSign,
  Clock,
  AlertCircle
} from 'lucide-react';

// shadcn components
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../tabs';
import { Alert, AlertDescription } from '../../alert';

// Import custom components
import ServiceDetailsFormShadcn from '../../forms/ServiceDetailsForm/ServiceDetailsFormShadcn';
import PricingAndDurationSection from '../PricingAndDurationSection/PricingAndDurationSection';

import { cn } from '../../../../utils/cn';

const ServiceEditorShadcn = ({
  service,
  serviceComponents = [],
  onUpdateService,
  onCreateComponent,
  onUpdateComponent,
  onDeleteComponent,
  savingService = false,
  savingComponents = false
}) => {
  const [activeTab, setActiveTab] = useState('details');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Reset tab when service changes
  useEffect(() => {
    setActiveTab('details');
    setHasUnsavedChanges(false);
  }, [service?.id]);

  // Empty state when no service selected
  if (!service) {
    return (
      <Card className="h-full flex items-center justify-center">
        <CardContent className="text-center">
          <Settings className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <CardTitle className="mb-2">Select a Service</CardTitle>
          <CardDescription className="max-w-sm">
            Choose a service from the list to view and edit its details and components.
          </CardDescription>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full flex flex-col">
      {/* Header */}
      <CardHeader className="flex-shrink-0">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            {/* Service Color Indicator */}
            <div
              className="w-4 h-4 rounded-full border-2 border-border"
              style={{ backgroundColor: service.color_code || '#8B5CF6' }}
            />

            <div>
              <CardTitle>{service.name}</CardTitle>
              <CardDescription>
                {service.active ? 'Active service' : 'Inactive service'}
              </CardDescription>
            </div>
          </div>

          {/* Unsaved Changes Indicator */}
          {hasUnsavedChanges && (
            <Alert className="w-auto">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-sm font-medium">
                Unsaved changes
              </AlertDescription>
            </Alert>
          )}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="details" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Service Details
            </TabsTrigger>
            <TabsTrigger value="pricing" className="flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Pricing & Duration
              {serviceComponents.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 rounded-full text-xs font-semibold bg-primary/20 text-primary">
                  {serviceComponents.length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </CardHeader>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <Tabs value={activeTab} className="h-full">
          <TabsContent value="details" className="h-full m-0">
            <ServiceDetailsFormShadcn
              service={service}
              onUpdate={onUpdateService}
              onUnsavedChanges={setHasUnsavedChanges}
              saving={savingService}
            />
          </TabsContent>

          <TabsContent value="pricing" className="h-full m-0">
            <div className="p-6">
              <PricingAndDurationSection
                service={service}
                components={serviceComponents}
                onUpdateService={onUpdateService}
                onCreateComponent={onCreateComponent}
                onUpdateComponent={onUpdateComponent}
                onDeleteComponent={onDeleteComponent}
                saving={savingComponents}
              />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Card>
  );
};

export default ServiceEditorShadcn;